/**
 * My People Page — Person-Centered Portal Home (DRR-134, DRR-135)
 *
 * The person-centered home view. Starts from people (SUBJECT actors),
 * expands to their world: active care state, tasks, care team.
 *
 * This is the entry point for roles whose work centers on specific people:
 * CNAs, nurses, social workers, DON in rounds mode.
 *
 * Subject cards are lightweight (actor data only).
 * Task detail loads on expansion (avoids N+1 queries).
 *
 * CONSTRAINTS:
 * - NO hardcoded subject lists (fetched via getActorsByType)
 * - Subject label from settingBundle.subjectLabel ("Resident" for ALF, "Client" for home care)
 * - Navigate to /journey/:id for full constellation expansion
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Skeleton,
  Alert,
  Button,
  ActionIcon,
  Collapse,
  Divider,
} from '@mantine/core';
import {
  IconUser,
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconChecklist,
  IconArrowRight,
  IconHeartHandshake,
  IconFocus2,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { useTenantId, useScopeCheck } from '@/auth';
import {
  useMySubjectsQuery, // DRR-135: Secure backend-filtered query
  useGetTasksBySubjectQuery,
  useGetSettingNavigationQuery,
  useConstellationsByActorQuery,
  GqlTaskStatus,
  GqlTaskPriority,
} from '@/generated/graphql';
import { CareBriefingPanel } from './CareBriefingPanel';
import { useFocusContext } from '@/contexts/FocusContext';
import { Scope } from '@ferp/core/generated/registries';

// ============================================================================
// PRIORITY COLORS — derived from GqlTaskPriority enum values
// ============================================================================

const PRIORITY_COLOR: Record<string, string> = {
  [GqlTaskPriority.CRITICAL]: 'red',
  [GqlTaskPriority.HIGH]: 'orange',
  [GqlTaskPriority.NORMAL]: 'yellow',
  [GqlTaskPriority.LOW]: 'blue',
};

const PRIORITY_ORDER = [
  GqlTaskPriority.CRITICAL,
  GqlTaskPriority.HIGH,
  GqlTaskPriority.NORMAL,
  GqlTaskPriority.LOW,
];

// ============================================================================
// SUBJECT CARD — lightweight card, expands to show tasks on demand
// ============================================================================

interface SubjectCardProps {
  readonly id: string;
  readonly name: string;
  readonly category?: string | null;
  readonly description?: string | null;
  readonly active: boolean;
  readonly subjectLabel: string;
}

function SubjectCard({ id, name, category, description, active, subjectLabel }: SubjectCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const navigate = useNavigate();
  const tenantId = useTenantId();
  const { setFocus, focusedSubject } = useFocusContext();
  const isCurrentlyFocused = focusedSubject === id;

  // Load tasks only when the card is expanded (on-demand, not N+1 on mount)
  const { data: taskData, loading: taskLoading } = useGetTasksBySubjectQuery({
    variables: { subjectActorId: id },
    skip: !expanded || !tenantId,
  });

  // DRR-126/134: Care team — constellation memberships for this subject
  const { data: constellationData } = useConstellationsByActorQuery({
    variables: { actorId: id },
    fetchPolicy: 'cache-first',
  });
  const careTeamMembers = constellationData?.constellationsByActor?.memberships ?? [];

  const tasks = taskData?.getTasksBySubject ?? [];
  const activeTasks = tasks.filter(
    (t) => t.status !== GqlTaskStatus.COMPLETED && t.status !== GqlTaskStatus.CANCELLED
  );
  const criticalTasks = activeTasks.filter((t) => t.priority === GqlTaskPriority.CRITICAL);
  const highTasks = activeTasks.filter((t) => t.priority === GqlTaskPriority.HIGH);

  return (
    <Card withBorder style={{ transition: 'box-shadow 150ms ease' }}>
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group
            gap="sm"
            wrap="nowrap"
            style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
            onClick={() => navigate(`/ferp-core/journey?subject=${id}&tab=tasks`)}
          >
            <ThemeIcon
              color={active ? 'teal' : 'gray'}
              variant="light"
              size={38}
              radius="xl"
            >
              <IconUser size={20} />
            </ThemeIcon>
            <div style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" lineClamp={1}>{name}</Text>
              {category && (
                <Text size="xs" c="dimmed">{category}</Text>
              )}
            </div>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {!active && (
              <Badge size="xs" color="gray" variant="light">Inactive</Badge>
            )}
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? `Collapse ${subjectLabel}` : `Expand ${subjectLabel}`}
            >
              {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            </ActionIcon>
          </Group>
        </Group>

        {description && !expanded && (
          <Text size="xs" c="dimmed" lineClamp={2}>{description}</Text>
        )}

        {/* Care team summary — Ring 1 constellation participants */}
        {careTeamMembers.length > 0 && (
          <Group gap={4}>
            {careTeamMembers
              .filter((m) => m.ring === 1)
              .slice(0, 4)
              .map((m) => (
                <Badge
                  key={`${m.constellationId}-${m.edgeType}`}
                  size="xs"
                  variant="dot"
                  color="teal"
                >
                  {m.role || m.edgeType}
                </Badge>
              ))}
            {careTeamMembers.filter((m) => m.ring === 1).length > 4 && (
              <Badge size="xs" variant="light" color="gray">
                +{careTeamMembers.filter((m) => m.ring === 1).length - 4}
              </Badge>
            )}
          </Group>
        )}

        {/* Expanded: tasks + actions */}
        <Collapse in={expanded}>
          <Stack gap="xs">
            <Divider />

            {taskLoading && (
              <>
                <Skeleton height={16} width="80%" />
                <Skeleton height={16} width="60%" />
              </>
            )}
            {!taskLoading && activeTasks.length === 0 && (
              <Text size="xs" c="dimmed">No active tasks today.</Text>
            )}
            {!taskLoading && activeTasks.length > 0 && (
              <>
                <Group gap="xs">
                  {criticalTasks.length > 0 && (
                    <Badge size="xs" color="red" variant="filled">
                      {criticalTasks.length} Critical
                    </Badge>
                  )}
                  {highTasks.length > 0 && (
                    <Badge size="xs" color="orange" variant="light">
                      {highTasks.length} High
                    </Badge>
                  )}
                  <Text size="xs" c="dimmed">
                    {activeTasks.length} active task{activeTasks.length === 1 ? '' : 's'}
                  </Text>
                </Group>

                {/* Top 3 urgent tasks inline */}
                <Stack gap={4}>
                  {activeTasks
                    .sort((a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority))
                    .slice(0, 3)
                    .map((task) => (
                      <Group key={task.id} gap="xs" wrap="nowrap">
                        <Badge
                          size="xs"
                          color={PRIORITY_COLOR[task.priority] ?? 'gray'}
                          variant="dot"
                          style={{ flexShrink: 0 }}
                        >
                          {task.priority}
                        </Badge>
                        <Text size="xs" lineClamp={1}>{task.name}</Text>
                      </Group>
                    ))}
                  {activeTasks.length > 3 && (
                    <Text size="xs" c="dimmed">+{activeTasks.length - 3} more</Text>
                  )}
                </Stack>
              </>
            )}

            {/* Actions */}
            <Group gap="xs" mt="xs">
              <Button
                size="xs"
                variant={isCurrentlyFocused ? 'filled' : 'light'}
                color={isCurrentlyFocused ? 'blue' : undefined}
                leftSection={<IconFocus2 size={12} />}
                onClick={() => setFocus(id, name)}
              >
                {isCurrentlyFocused ? 'Focused' : 'Focus'}
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconHeartHandshake size={12} />}
                onClick={() => setBriefingOpen(true)}
              >
                Brief
              </Button>
              <Button
                size="xs"
                variant="subtle"
                rightSection={<IconArrowRight size={12} />}
                onClick={() => navigate(`/ferp-core/tasks?subject=${id}`)}
              >
                All Tasks
              </Button>
            </Group>
          </Stack>
        </Collapse>
      </Stack>

      <CareBriefingPanel
        opened={briefingOpen}
        onClose={() => setBriefingOpen(false)}
        subjectId={id}
        subjectName={name}
        subjectCategory={category}
        subjectDescription={description}
        subjectLabel={subjectLabel}
      />
    </Card>
  );
}

// ============================================================================
// MY PEOPLE PAGE
// ============================================================================

export function MyPeoplePage() {
  const { hasScope } = useScopeCheck();

  // DRR-135: Subject label from backend settingBundle.subjectLabel (e.g. "Resident" for ALF).
  // Fallback: "Person" / "People" — universal default for non-healthcare industries.
  const { data: settingData } = useGetSettingNavigationQuery({ fetchPolicy: 'cache-first' });
  const rawSubjectLabel = settingData?.settingBundle?.subjectLabel;
  const subjectLabel = rawSubjectLabel && rawSubjectLabel.length > 0 ? rawSubjectLabel : 'Person';
  const subjectLabelPlural = subjectLabel === 'Person' ? 'People' : `${subjectLabel}s`;

  // DRR-135: Secure backend-filtered query replaces all frontend filtering logic.
  // The mySubjects resolver enforces visibility rules SERVER-SIDE:
  // - facility:admin OR clinical:full → see all subjects
  // - Otherwise → see only constellation-assigned subjects
  // - No assignments + not admin → empty list (no fallback to "all")
  //
  // This is the ONLY query needed — no more useGetActorsByTypeQuery + client filtering.
  const { data, loading, error } = useMySubjectsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const subjects = data?.mySubjects ?? [];
  const activeSubjects = subjects.filter((s) => s.active);

  // For display logic only — admins see "No subjects found", non-admins see "no assignments"
  const canSeeAllSubjects = hasScope(Scope.FacilityAdmin) || hasScope(Scope.ClinicalFull);

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>My {subjectLabelPlural}</Title>
            <Text c="dimmed" size="sm">
              {loading ? 'Loading...' : `${activeSubjects.length} active ${subjectLabelPlural.toLowerCase()}`}
            </Text>
          </div>
          <Badge variant="light" color="teal" size="lg">
            <Group gap={4}>
              <IconChecklist size={14} />
              Person-centered view
            </Group>
          </Badge>
        </Group>

        {/* Error */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Could not load {subjectLabelPlural.toLowerCase()}. Please refresh.
          </Alert>
        )}

        {/* Loading skeleton */}
        {loading && (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} withBorder>
                <Group gap="sm">
                  <Skeleton circle height={38} />
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Skeleton height={14} width="70%" />
                    <Skeleton height={12} width="40%" />
                  </Stack>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        )}

        {/* Empty state */}
        {!loading && !error && subjects.length === 0 && (
          <Alert icon={<IconUser size={16} />} color="blue" variant="light">
            {canSeeAllSubjects ? (
              <>No {subjectLabelPlural.toLowerCase()} found. {subjectLabelPlural} are added through the intake process.</>
            ) : (
              <>You have no assigned {subjectLabelPlural.toLowerCase()}. Contact your supervisor to be added to care teams.</>
            )}
          </Alert>
        )}

        {/* Subject grid */}
        {!loading && subjects.length > 0 && (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {subjects.map((subject) => (
              <SubjectCard
                key={subject.id}
                id={subject.id}
                name={subject.name}
                category={subject.category}
                description={subject.description}
                active={subject.active}
                subjectLabel={subjectLabel}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
}
